import { useState, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { TableProps } from 'antd';
import { SorterResult } from 'antd/lib/table/interface';
import { Divider, Fx, Spin, Stack } from 'Components';
import { createSortDescription } from 'Components/Table/createColumns';
import { useStores } from 'Hooks/useStore';
import { getIsPending } from 'Stores/util';
import { DashboardContext } from 'Pages/Dashboard/DashboardContext';
import DashboardFilters from 'Pages/Dashboard/DashboardFilters/DashboardFilters';
import { DashboardFiltersValues } from 'Pages/Dashboard/Dashboard.types';
import {
	getAbsDiffValue,
	getTrendDirection
} from 'Pages/Dashboard/Dashboard.helpers';
import {
	OverviewGroupSummary,
	OverviewScores,
	WidgetData
} from 'Services/Api/Dashboard/Types';
import StatsCard from './components/StatsCard/StatsCard';
import GroupSummaryTable from './GroupSummaryTable/GroupSummaryTable';
import styles from './NewDashboard.module.scss';
import Heading from 'LearningManager/components/Heading';

type ScoreKey = keyof Pick<
	OverviewScores,
	'phishingScore' | 'elearningScore' | 'saqCompliance' | 'scanning'
>;

const STATS_CARD_CONFIG: Array<{
	key: ScoreKey;
	title: string;
	tooltip: string;
}> = [
	{
		key: 'phishingScore',
		title: 'Phishing',
		tooltip:
			'Percentage of users passing simulated phishing attempts. Includes a one-month lookback to track progress.'
	},
	{
		key: 'elearningScore',
		title: 'Training',
		tooltip:
			'Completion percentage of assigned learning paths in your organisation. Includes a one-month lookback to track progress.'
	},
	{
		key: 'saqCompliance',
		title: 'SAQ Compliance',
		tooltip: 'SAQ Compliance score.'
	},
	{
		key: 'scanning',
		title: 'Scanning',
		tooltip: 'Scanning score.'
	}
];

function NewDashboard() {
	const stores = useStores();
	const { dashboardStore } = stores;
	const [filterValues, setFilterValues] = useState<DashboardFiltersValues>(
		{}
	);
	const [hasProperties, setHasProperties] = useState(true);

	const { overview, status } = dashboardStore;

	useEffect(() => {
		dashboardStore.getOverviewScores(filterValues);
		dashboardStore.getOverviewGroupSummary(filterValues);
		// eslint-disable-next-line
	}, [filterValues]);

	useEffect(() => {
		return () => {
			stores.clearDashboardStore();
		};
		// eslint-disable-next-line
	}, []);

	const onFiltersChange = (value: DashboardFiltersValues) => {
		setFilterValues((prev) => ({ ...prev, ...value }));
	};

	const handleTableChange: TableProps<OverviewGroupSummary>['onChange'] = (
		_,
		__,
		sorter
	) => {
		const sort = createSortDescription<OverviewGroupSummary>(
			sorter as SorterResult<OverviewGroupSummary>
		);
		dashboardStore.getOverviewGroupSummary({
			...overview.groupSummary.params,
			sort
		});
	};

	const getCardData = (key: ScoreKey) => {
		const data = overview.scores?.[key] as WidgetData | undefined | null;
		return {
			value: data?.current ?? null,
			trend: getAbsDiffValue(data?.current ?? null, data?.hystorical),
			trendDirection: getTrendDirection(
				data?.current ?? null,
				data?.hystorical
			)
		};
	};

	const scoresLoading = getIsPending(status.getOverviewScores);

	return (
		<DashboardContext.Provider
			value={{ filters: filterValues, hasProperties }}
		>
			<Fx>
				<Fx column grow={1}>
					<Stack>
						<div className={styles.pageHeader}>
							<Fx align="middle" justify="space-between">
								<Heading
									title="Dashboard"
									subHeading="Overview of metrics and performance."
									titleClassName={styles.pageTitle}
									subTitleClassName={styles.pageSubTitle}
								/>
								<DashboardFilters
									onChange={onFiltersChange}
									onSetHasProperties={setHasProperties}
								/>
							</Fx>
						</div>

						{scoresLoading ? (
							<div className={styles.spinWrapper}>
								<Spin />
							</div>
						) : (
							<Fx className={styles.statsRow} wrap>
								{STATS_CARD_CONFIG.map(
									({ key, title, tooltip }) => (
										<Fx
											key={key}
											grow={1}
											className={styles.statCardWrapper}
										>
											<StatsCard
												title={title}
												tooltip={tooltip}
												{...getCardData(key)}
											/>
										</Fx>
									)
								)}
							</Fx>
						)}

						<GroupSummaryTable
							data={overview.groupSummary.data}
							loading={getIsPending(
								status.getOverviewGroupSummary
							)}
							onChange={handleTableChange}
						/>

						<Divider className={styles.divider} />
					</Stack>
				</Fx>
			</Fx>
		</DashboardContext.Provider>
	);
}

export default observer(NewDashboard);
